import { useState } from 'react';

import { CostCalculationExternalSurcharge } from 'generated';
import { useTranslation } from 'react-i18next';

import { formatByLocale, formatTime, fromISOString } from 'utils/date';
import { displayCurrency, displayPercentage } from 'utils/misc';

import { queries } from 'RestAPIs/rapini';

import { Checkbox } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridValidRowModel
} from '@mui/x-data-grid-pro';

import { AddEditCostCalculationExternalSurcharge } from 'components/shared/CostCalculationExternalSurcharge/AddEditCostCalculationExternalSurcharge';
import TableCRUDLayout from '../CRUD/TableCRUDLayout';

export type CostCalculationExternalsSurchargeGrid = {
  employmentId?: number;
  organizationalUnitId?: number;
};

const dateFormatter = (params: GridRenderCellParams<any, string | Date, any>) =>
  (params.value && formatByLocale(fromISOString(params.value), 'P')) || '-';
const timeFormatter = (params: GridRenderCellParams<any, string | Date, any>) =>
  (params.value && formatTime(fromISOString(params.value))) || '-';
const booleanFormatter = (params: GridRenderCellParams<any, boolean, any>) => (
  <Checkbox checked={params.value} />
);

export function CostCalculationExternalSurchargeGrid(
  props: CostCalculationExternalsSurchargeGrid
) {
  const { employmentId, organizationalUnitId } = props;

  const { t } = useTranslation();

  const columnsIrregularity: GridColDef<GridValidRowModel>[] = [
    {
      field: 'description',
      headerName: t('description'),
      flex: 1,
      minWidth: 200
    }
  ];
  if (organizationalUnitId) {
    columnsIrregularity.push({
      field: 'function',
      headerName: t('function'),
      renderCell: ({ value }) => (value && value.description) || '-',
      flex: 1,
      minWidth: 200
    });
  }
  columnsIrregularity.push(
    {
      field: 'validFrom',
      headerName: t('validFrom'),
      renderCell: dateFormatter
    },
    {
      field: 'validTo',
      headerName: t('validTo'),
      renderCell: dateFormatter
    },
    {
      field: 'monday',
      headerName: t('weekday.monday'),
      renderCell: booleanFormatter,
      width: 80
    },
    {
      field: 'tuesday',
      headerName: t('weekday.tuesday'),
      renderCell: booleanFormatter,
      width: 80
    },
    {
      field: 'wednesday',
      headerName: t('weekday.wednesday'),
      renderCell: booleanFormatter,
      width: 80
    },
    {
      field: 'thursday',
      headerName: t('weekday.thursday'),
      renderCell: booleanFormatter,
      width: 80
    },
    {
      field: 'friday',
      headerName: t('weekday.friday'),
      renderCell: booleanFormatter,
      width: 80
    },
    {
      field: 'saturday',
      headerName: t('weekday.saturday'),
      renderCell: booleanFormatter,
      width: 80
    },
    {
      field: 'sunday',
      headerName: t('weekday.sunday'),
      renderCell: booleanFormatter,
      width: 80
    },
    {
      field: 'timeFrom',
      headerName: t('startTime'),
      renderCell: timeFormatter,
      width: 80
    },
    {
      field: 'timeTo',
      headerName: t('endTime'),
      renderCell: timeFormatter,
      width: 80
    },
    {
      field: 'amount',
      headerName: t('baseAmount'),
      renderCell: ({ value, row }) => {
        return row.surchargeType === 'AMOUNT'
          ? (value && displayCurrency(value)) || '-'
          : '-';
      }
    },
    {
      field: 'percentage',
      headerName: t('percentage'),
      renderCell: ({ value, row }) => {
        return row.surchargeType === 'PERCENTAGE'
          ? (value && displayPercentage(value)) || '-'
          : '-';
      }
    }
  );

  const { data: surcharges } = queries.useGetCostCalculationExternalSurcharges(
    employmentId,
    organizationalUnitId
  );

  const [
    shouldShowCostCalculationExternalSurcharge,
    setShouldShowCostCalculationExternalSurcharge
  ] = useState<boolean>(false);

  const [
    costCalculationExternalSurcharge,
    setCostCalculationExternalSurcharge
  ] = useState<CostCalculationExternalSurcharge | null>(null);

  function handleAddCostCalculationExternalSurchargeClick() {
    setCostCalculationExternalSurcharge(null);
    setShouldShowCostCalculationExternalSurcharge(true);
  }
  function handleEditCostCalculationExternalSurchargeClick(id: number) {
    setCostCalculationExternalSurcharge(
      surcharges?.find(
        (setting) => setting.id == id
      ) as CostCalculationExternalSurcharge
    );
    setShouldShowCostCalculationExternalSurcharge(true);
  }

  function handleClose() {
    setShouldShowCostCalculationExternalSurcharge(false);
  }

  return (
    <>
      <TableCRUDLayout
        columns={columnsIrregularity}
        rows={surcharges || []}
        shouldRenderToolBar={false}
        renderFormContainer={false}
        editable={false}
        onEditClick={(id) =>
          handleEditCostCalculationExternalSurchargeClick(+id)
        }
        onAddClick={() => handleAddCostCalculationExternalSurchargeClick()}
        addRowDialog={
          <AddEditCostCalculationExternalSurcharge
            isOpen={shouldShowCostCalculationExternalSurcharge}
            employmentId={employmentId}
            organizationalUnitId={organizationalUnitId}
            handleClose={handleClose}
            surcharge={costCalculationExternalSurcharge}
          />
        }
      />
    </>
  );
}
